import api from '../httpClient';

export type TaxonomyItemType = 'IN' | 'PKG' | 'BEV' | 'SUP';

export interface IngredientDepartmentOption {
  id: string;
  name: string;
  sortOrder: number;
  subgroupNames: string[];
}

export interface IngredientGroupOption {
  id: string;
  name: string;
  itemType: TaxonomyItemType;
  sortOrder: number;
  departments: IngredientDepartmentOption[];
}

export interface IngredientTagOption {
  id: string;
  displayName: string;
  name: string;
  sortOrder: number;
}

export interface IngredientCreationMetadataResponse {
  success: boolean;
  data: {
    itemType: TaxonomyItemType;
    itemTypeLabel?: string;
    sourceName: string;
    sourceId: string;
    ingredientId: string;
    requiredFields: string[];
    groups: IngredientGroupOption[];
    tags: {
      dietary: IngredientTagOption[];
      allergen: IngredientTagOption[];
      intolerance: IngredientTagOption[];
    };
    enums: {
      processingStates: string[];
      physicalStates: string[];
      dietaryClasses: string[];
    };
  };
}

export interface CreateNutritionistIngredientRequest {
  itemType?: TaxonomyItemType;
  sourceId?: string;
  sourceCode?: string | null;
  sourceFoodName?: string | null;
  name: string;
  description?: string | null;
  aliases?: string[];
  groupId: string;
  departmentId: string;
  processingState: string;
  physicalState: string;
  dietaryClass: string;
  dietaryTagIds?: string[];
  allergenTagIds?: string[];
  intoleranceTagIds?: string[];
  nutritionPer100g?: Record<string, number>;
  glycemicIndex?: number | null;
  glycemicLoad?: number | null;
  servingOverrides?: Array<{
    servingId: string;
    servingSize?: number | null;
    grams: number;
    ml?: number | null;
    isDefault?: boolean;
  }>;
  defaultServingId?: string | null;
  referenceWeight: number;
  referenceUnit: string;
  isBranded?: boolean | null;
  yieldFactor?: number | null;
  retentionFactor?: number | null;
}

export interface CreateNutritionistIngredientResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    sourceId: string;
    sourceName: string;
    ingredientId: string;
    itemType: TaxonomyItemType;
    name: string;
    groupId: string;
    groupName: string;
    departmentId: string;
    departmentName: string;
    processingState: string;
    physicalState: string;
    dietaryClass: string;
    referenceWeight: number;
    referenceUnit: string;
    sourceCode?: string | null;
    sourceFoodName?: string | null;
    isBranded?: boolean | null;
    yieldFactor?: number | null;
    retentionFactor?: number | null;
    createdAt: string;
  };
}

export interface IngredientDuplicateMatch {
  id: string;
  sourceId: string | null;
  foodCode: string | null;
  sourceCode: string | null;
  source: 'excel_import' | 'internal';
  sourceLabel: string;
  name: string;
  displayName: string | null;
  sourceFoodName: string | null;
  aliases: string[];
  groupId: string | null;
  groupName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string | null;
  matchedFields: string[];
  exactMatch: boolean;
}

export interface IngredientDuplicateSearchResponse {
  success: boolean;
  data: {
    query: string;
    matches: IngredientDuplicateMatch[];
    count: number;
    hasExactMatch: boolean;
  };
}

export const getIngredientCreationMetadata = async (
  itemType: TaxonomyItemType = 'IN'
): Promise<IngredientCreationMetadataResponse> => {
  const response = await api.get<IngredientCreationMetadataResponse>('/ingredients/custom/metadata', {
    params: { itemType },
  });
  return response.data;
};

export const createNutritionistIngredient = async (
  payload: CreateNutritionistIngredientRequest
): Promise<CreateNutritionistIngredientResponse> => {
  const response = await api.post<CreateNutritionistIngredientResponse>('/ingredients/custom', payload);
  return response.data;
};

export const searchIngredientDuplicates = async (
  query: string,
  limit = 20
): Promise<IngredientDuplicateSearchResponse> => {
  const response = await api.get<IngredientDuplicateSearchResponse>('/ingredients/custom/duplicates', {
    params: { query, limit },
  });
  return response.data;
};
